/**
 * Human-readable, deterministic label of an address input. The `{zip} {city}`
 * prefix is stable across every entry, the `— {street}` suffix disambiguates
 * multiple addresses in the same city. Absent zip/city (an incomplete #478
 * entry) yields a well-formed but visibly odd label so callers cannot
 * confuse it with a real key.
 */
export function labelOfAddress(addr) {
    const suffix = addr.address ? ` — ${addr.address}` : "";
    const zipCity = [addr.zip, addr.city].filter(Boolean).join(" ").trim();
    return `${zipCity || "(no zip/city)"}${suffix}`;
}
// Julien's departure address is missing from #478. Placeholder for the first
// pass — layer A's mountain assertions are independent of it; only the daily
// departure/return legs depend on this. To be confirmed with Julien before
// building any window assertion on top of departure/return.
export const JULIEN_DEPARTURE_PLACEHOLDER = {
    zip: "44000",
    city: "Nantes",
    address: "Place du Commerce",
};
/**
 * One entry per unique address across the 7 agendas (#478). Names are
 * kebab-ish keys; the labels (via {@link labelOfAddress}) are the STABLE
 * KEYS of {@link JulienScenariosFixture.addresses}. Duplicates across days
 * (e.g. Julien coming back to the same client house) reuse the same entry.
 */
export const JULIEN_AGENDA_ADDRESSES = {
    // Mer 8/07
    suceLaCrubraie: { zip: "44240", city: "Sucé-sur-Erdre", address: "La Crubraie" },
    montlucChenCreux: {
        zip: "44360",
        city: "Saint-Étienne-de-Montluc",
        address: "9 bis rue du Chêne creux",
    },
    blainGuerinais: { zip: "44130", city: "Blain", address: "La Guérinais" },
    // Ven 10/07
    pornichetClosRoux: { zip: "44380", city: "Pornichet", address: "40 chemin du Clos Roux" },
    prinquiauChudais: { zip: "44260", city: "Prinquiau", address: "34 route de la Chudais" },
    // Lun 13/07
    brevinFief: { zip: "44250", city: "Saint-Brevin-les-Pins", address: "Chemin du Fief" },
    // "Chemin du Bourgneuf" — incomplete in #478 (no zip, no city). We keep the
    // entry so the day-by-day count matches, but geocoding legitimately fails
    // here → this leg is loudly missing from the fixture.
    incompleteBourgneuf: { zip: "", city: "", address: "Chemin du Bourgneuf" },
    frossayRobinerie: { zip: "44320", city: "Frossay", address: "La Robinerie" },
    // Jeu 16/07
    boiseauRocheres: { zip: "44640", city: "Saint-Jean-de-Boiseau", address: "Chemin des Rochères" },
    // Ven 17/07
    remaudiereChapelleMoines: {
        zip: "44430",
        city: "La Remaudière",
        address: "248 La Chapelle-aux-Moines",
    },
    vertouChampFleuri: { zip: "44120", city: "Vertou", address: "Rue du Champ Fleuri" },
    concellesEbrancherie: {
        zip: "44450",
        city: "Saint-Julien-de-Concelles",
        address: "41 l'Ebrancherie",
    },
    // Mar 21/07 — "46 la Colle, écurie de l'Arros" is a rural sub-address the
    // IGN cannot resolve at street level (score ~0.5): every leg touching it
    // is legitimately skipped from the fixture.
    blainColleArros: { zip: "44130", city: "Blain", address: "46 la Colle, écurie de l'Arros" },
    coueronMontagne: { zip: "44220", city: "Couëron", address: "28 route de la Montagne" },
    coueronPuigodeau: { zip: "44220", city: "Couëron", address: "Rue du Puigodeau" },
    coueronMortierNoues: { zip: "44220", city: "Couëron", address: "Rue du Mortier des Noues" },
    chapelleLaunayMaure: { zip: "44260", city: "La Chapelle-Launay", address: "La Haie de Maure" },
    // Mer 29/07
    sorinieresQuiltrie: { zip: "44840", city: "Les Sorinières", address: "4 La Quiltrie" },
    suceMoulinCasse: { zip: "44240", city: "Sucé-sur-Erdre", address: "Le Moulin Cassé" },
    suceTertreJuin: { zip: "44240", city: "Sucé-sur-Erdre", address: "Le Tertre Juin" },
};
/**
 * The 3 mountain reference routes Julien gave in #478 with a spoken
 * duration/distance. The fixture generator (B, #626) uses the addresses to
 * geocode and to route via ORS; assertions on the sums prove the RDV engine
 * is not silently falling back to bird-flight distances.
 */
export const JULIEN_MOUNTAIN_ADDRESSES = {
    neuvillerLaRoche: { zip: "67130", city: "Neuviller-la-Roche", address: "" },
    belmont67: { zip: "67130", city: "Belmont", address: "" },
    abreschviller: { zip: "57560", city: "Abreschviller", address: "" },
    raonSurPlaine: { zip: "88110", city: "Raon-sur-Plaine", address: "" },
    besseSaintAnastaise: { zip: "63610", city: "Besse-et-Saint-Anastaise", address: "" },
    // Julien wrote "Puy de Pariou 63870". The Puy Pariou is a volcanic crater
    // in the Chaîne des Puys, NOT routable in ORS `driving-car`. Julien's ~48
    // km reference corresponds to Besse → Orcines centre (48 km by road),
    // where the mounts are visited from. We resolve to Orcines commune centre.
    puyDePariou: { zip: "63870", city: "Orcines", address: "" },
};
/**
 * Addresses that the fixture generator is ALLOWED to skip without failing.
 * Two categories:
 * - `incompleteBourgneuf`: intrinsically incomplete input (no zip/city).
 *   Layer A will throw on lookup; that surfaces the gap.
 * - `blainColleArros`: sub-address IGN cannot resolve (score below 0.6).
 *   Layer A can either skip the day or fall back to city-level accuracy.
 * Anything ELSE that fails is a real regression to investigate.
 */
export const JULIEN_KNOWN_HARD_ADDRESS_KEYS = [
    "incompleteBourgneuf",
    "blainColleArros",
];
/**
 * `JULIEN_KNOWN_HARD_LABELS` (union avec l'allowlist propre à l'oracle) est
 * construit plus bas dans ce fichier, une fois `JULIEN_ORACLE_AGENDA_ADDRESSES`
 * déclaré — voir `JULIEN_ORACLE_KNOWN_HARD_ADDRESS_KEYS` (#990).
 */
// ---------------------------------------------------------------------------
// Oracle bench (#765/#766/#767) — Julien's real 60-day agenda, 20/07/2026
// ---------------------------------------------------------------------------
/**
 * Extension of the 7-day #478 bench with the real oracle test cases Julien
 * ran on 20/07/2026 ~17h (issues #765/#766/#767). Unlike `JULIEN_AGENDA_ADDRESSES`
 * (a single mono-day-horizon week), this covers the FULL `horizonDays: 60`
 * agenda from `JULIEN_ORACLE_NOW` (2026-07-20T17:00 Paris) — every event that
 * can influence the proxy pre-sort / matrix-scope top-40 the engine actually
 * walks (`packages-api/online-booking-server/src/planning/engine/engine.ts`,
 * `selectMatrixScope`). Source: canary `/api/events/solo` extract, 78 events
 * across 23 days between 21/07 and 15/09/2026 (the account's real state at
 * the moment of Julien's tests; the 20/07 events themselves are excluded —
 * `now` is 17:00 that day, past every working-hours window, so they can
 * never anchor a candidate). Plus 1 synthetic event (#990,
 * `nullepartvilleIntrouvable`, 2026-09-16) — 79 events across 24 days,
 * 21/07 → 16/09.
 */
export const JULIEN_ORACLE_NOW = "2026-07-20T17:00:00";
/** Julien's real settings (screenshot onglet "RDV en ligne", 20/07/2026) — weekdays only, two contiguous blocks. */
const ORACLE_WEEKDAY_SLOTS = [
    { start: "08:30", end: "13:30" },
    { start: "13:30", end: "16:30" },
];
export const JULIEN_ORACLE_WORKING_HOURS = {
    "1": ORACLE_WEEKDAY_SLOTS,
    "2": ORACLE_WEEKDAY_SLOTS,
    "3": ORACLE_WEEKDAY_SLOTS,
    "4": ORACLE_WEEKDAY_SLOTS,
    "5": ORACLE_WEEKDAY_SLOTS,
    "6": [],
    "7": [],
};
/** Single 60-min selector in the real settings UI, applied to all three edges. */
export const JULIEN_ORACLE_MAX_TRAVEL_TIME = {
    toFirst: 60,
    between: 60,
    toReturn: 60,
};
export const JULIEN_ORACLE_DEPARTURE = {
    zip: "44170",
    city: "Vay",
    address: "10 bis la Herrouinais",
};
/** The 3 oracle clients (#765/#766/#767), one per ticket. */
export const JULIEN_ORACLE_CLIENT_ADDRESSES = {
    coueronMontagne765: { zip: "44220", city: "Couëron", address: "28 route de la Montagne" },
    plaineSurMerMasseries766: {
        zip: "44770",
        city: "La Plaine-sur-Mer",
        address: "Chemin des Masseries",
    },
    joueSurErdreMulonniere767: { zip: "44440", city: "Joué-sur-Erdre", address: "La Mulonnière" },
};
/**
 * 6 more oracle clients from Julien's second batch of tests the same evening
 * (20/07/2026, 19:28→20:42), issues #770/#772/#776/#780/#781/#782. Same
 * `JULIEN_ORACLE_NOW`/`JULIEN_ORACLE_AGENDA`/settings — only the client
 * address + slot duration change per ticket (all "parage physiologique ×1
 * cheval" = 30 min).
 */
export const JULIEN_ORACLE_CLIENT_ADDRESSES_2 = {
    prinquiauChudais770: { zip: "44260", city: "Prinquiau", address: "34 route de la Chudais" },
    hauteGoulainePierrePlate772: {
        zip: "44115",
        city: "Haute-Goulaine",
        address: "Impasse de la Pierre Plate",
    },
    // Same physical spot as `JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeMoulinCasse` (an
    // existing agenda address) — Julien is testing a NEW client request at a
    // location he already visits recurrently. Kept as a separate label (ticket's
    // own capitalization) so the client-side SCORE_LIMIT gate (D2) is evaluated
    // independently of the agenda-side entry, which is never re-gated (see #765
    // investigation above).
    suceMoulinCasse776: { zip: "44240", city: "Sucé-sur-Erdre", address: "Le Moulin Cassé" },
    // Same physical spot as `JULIEN_ORACLE_AGENDA_ADDRESSES.erbray6LeBignon`.
    erbray6LeBignon780: { zip: "44110", city: "Erbray", address: "6 Le Bignon" },
    // Same physical spot as `JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere*`.
    couffeLaCribotiere781: { zip: "44521", city: "Couffé", address: "La Cribotière" },
    /**
     * #782 — piquant : the client address is textually "46 La Colle, 44130
     * Blain", which is close to (but not identical to) the known-hard agenda
     * address `blain46LaColleEcurieDeLArros` (score 0.497, "écurie de l'Arros"
     * suffix) and identical in substance to `blain46LaColle` (score 0.956, no
     * suffix — already above SCORE_LIMIT). Whatever score THIS exact client
     * string gets on fresh geocoding, #782's own test report shows the real
     * tunnel answered with 5 concrete, coherent proposals — meaning production
     * did NOT reject this address on a low score. The only path that skips the
     * D2 score gate for a client-supplied address is a RECOGNIZED customer
     * (`RdvCustomerIdentification === "known"`, #594): an existing fiche's
     * stored position is read directly, never re-geocoded/re-gated — the exact
     * same asymmetry as an agenda event (D13, see #765 investigation). #782 is
     * therefore modelled with agenda semantics (no score gate) in the
     * generator, documenting a recognized-customer path rather than the
     * generic unknown-visitor tunnel gate.
     */
    blain46LaColle782: { zip: "44130", city: "Blain", address: "46 La Colle" },
};
/**
 * 5 more oracle clients (#785/#786/#788/#789/#790), same `JULIEN_ORACLE_NOW`
 * (20/07/2026 17h) and same agenda/settings as the first two batches — every
 * ticket's body states the identical test date, no second `now` needed here.
 */
export const JULIEN_ORACLE_CLIENT_ADDRESSES_3 = {
    // Same physical spot as `JULIEN_ORACLE_AGENDA_ADDRESSES.nortSurErdrePiaud`.
    piaudNortSurErdre785: { zip: "44390", city: "Nort-sur-Erdre", address: "Piaud" },
    // Same physical spot as `JULIEN_ORACLE_AGENDA_ADDRESSES.blainLaGuerinais`.
    guerinaisBlain786: { zip: "44130", city: "Blain", address: "La Guérinais" },
    laLandeNotreDameDesLandes788: {
        zip: "44130",
        city: "Notre-Dame-des-Landes",
        address: "La Lande",
    },
    // Même commune que `JULIEN_ORACLE_DEPARTURE` (Vay) mais PAS le domicile —
    // une autre adresse cliente dans Vay (#789).
    laRiviereVay789: { zip: "44170", city: "Vay", address: "La Rivière" },
    // Même adresse physique que `JULIEN_AGENDA_ADDRESSES.brevinFief` (bench #478,
    // horizon différent) — presqu'île/estuaire, hors périmètre présumé (#790).
    cheminDuFiefBrevin790: {
        zip: "44250",
        city: "Saint-Brévin-les-Pins",
        address: "Chemin du Fief",
    },
};
export const JULIEN_ORACLE_AGENDA_ADDRESSES = {
    blain46LaColleEcurieDeLArros: {
        zip: "44130",
        city: "Blain",
        address: "46 la colle, écurie de l'arros",
    },
    coueronRueDuPuigodeau: { zip: "44220", city: "Coueron", address: "Rue du puigodeau" },
    coueronRueDuMortierDesNouesFermeDuMarais: {
        zip: "44220",
        city: "Coueron",
        address: "Rue du mortier des noues (ferme du marais)",
    },
    laChapelleLaunayLaHaieDeMaure: {
        zip: "44260",
        city: "La chapelle launay",
        address: "La haie de Maure",
    },
    pornichet40CheminDuClosRoux: {
        zip: "44380",
        city: "Pornichet",
        address: "40 chemin du clos roux",
    },
    saintPereEnRetz1LaBasseParagere: {
        zip: "44320",
        city: "Saint Père en retz",
        address: "1 la basse paragere",
    },
    saintPereEnRetzLeBoisRegnier: {
        zip: "44320",
        city: "Saint Père en retz",
        address: "Le bois regnier",
    },
    corsept13BisLaBigotais: { zip: "44560", city: "Corsept", address: "13 bis la bigotais" },
    saintJeanDeBoiseauRueDesPicarderies: {
        zip: "44640",
        city: "Saint Jean de Boiseau",
        address: "rue des picarderies",
    },
    saintJeanDeBoiseauRueDesPicarderie: {
        zip: "44640",
        city: "Saint jean de boiseau",
        address: "Rue des picarderie",
    },
    marsacSurDon6LaunayDeBouin: { zip: "44170", city: "Marsac sur don", address: "6 Launay de Bouin" },
    marsacSurDon25LaunayDeBoin: { zip: "44170", city: "Marsac Sur Don", address: "25 Launay de Boin" },
    laChevallerais5LePatisJamet: {
        zip: "44810",
        city: "La Chevallerais",
        address: "5 le patis jamet",
    },
    blain46LaColle: { zip: "44130", city: "Blain", address: "46 la colle" },
    saintMichelChefChefLaGrenouillere: {
        zip: "44730",
        city: "Saint Michel chef chef",
        address: "La grenouillere",
    },
    saintMichelChefChefMoulinDeLaSicaudais: {
        zip: "44730",
        city: "Saint Michel chef chef",
        address: "Moulin de la sicaudais",
    },
    saintMichelChefChefMoulinDeLaSicaudais2: {
        zip: "44730",
        city: "Saint Michel Chef Chef",
        address: "moulin de la sicaudais",
    },
    leGavre18RueDesPuits: { zip: "44130", city: "Le gavre", address: "18 rue des puits" },
    lesSorinieres4LaQuiltrie: { zip: "44840", city: "Les Sorinières", address: "4 la quiltrie" },
    suceSurErdreLeMoulinCasse: { zip: "44240", city: "Suce sur erdre", address: "Le moulin cassé" },
    suceSurErdreLeMoulinCasse2: { zip: "44240", city: "Sucé sur erdre", address: "Le moulin cassé" },
    suceSurErdreLeTertreJuin: { zip: "44240", city: "Sucé sur Erdre", address: "Le Tertre Juin" },
    suceSurErdreLeTertreJuin2: { zip: "44240", city: "Sucé sur Erdre", address: "Le tertre juin" },
    suceSurErdreLeTertreJuin3: { zip: "44240", city: "Sucé sur Erdre", address: "Le Tertre juin" },
    bouinLesVerreries: { zip: "85230", city: "Bouin", address: "Les verreries" },
    boisDeCeneLeMarcheClair: { zip: "85710", city: "Bois de céné", address: "Le marché clair" },
    saintMemeLeBoisFoucaud: { zip: "44270", city: "Saint même", address: "Le Bois foucaud" },
    plesseLaTahetrie: { zip: "44630", city: "Plesse", address: "La Tahêtrie" },
    nortSurErdrePiaud: { zip: "44390", city: "Nort sur erdre", address: "Piaud" },
    treillieresFayau: { zip: "44119", city: "Treillieres", address: "Fayau" },
    carquefou12Maubreuil: { zip: "44470", city: "Carquefou", address: "12 maubreuil" },
    vallonsDeLErdreLesBassesProvostieres: {
        zip: "44540",
        city: "Vallons de l'erdre",
        address: "Les basses provostieres",
    },
    abbaretz7LaRainais: { zip: "44170", city: "Abbaretz", address: "7 la rainais" },
    riailleLaVerrerie: { zip: "44440", city: "Riaille", address: "La Verrerie" },
    erbray6LeBignon: { zip: "44110", city: "Erbray", address: "6 le bignon" },
    leGavre7RueDuVerreDeLaitLeHautLuc: {
        zip: "44130",
        city: "Le gavre",
        address: "7 rue du verre de lait- le haut luc",
    },
    pontSaintMartin30CheminDeLaVerdure: {
        zip: "44860",
        city: "Pont Saint Martin",
        address: "30 Chemin de la verdure",
    },
    pontSaintMartinCheminDeLaVerdure: {
        zip: "44860",
        city: "Pont Saint Martin",
        address: "Chemin de la verdure",
    },
    laChapelleStFlorentRueDeLEvre: {
        zip: "49410",
        city: "La chapelle st Florent",
        address: "Rue de l'evre",
    },
    saintJulienDeConcellesIleDeLaChesnaie: {
        zip: "44450",
        city: "Saint Julien de Concelles",
        address: "Ile de la Chesnaie",
    },
    laChapelleHeulin7LaMorsonniere: {
        zip: "44330",
        city: "La Chapelle Heulin",
        address: "7 la Morsonniere",
    },
    leLorouxBottereau84RouteDeLaCoulee: {
        zip: "44430",
        city: "Le loroux bottereau",
        address: "84 route de la coulée",
    },
    vertouRueDuChampFleuri: { zip: "44120", city: "Vertou", address: "Rue du champ fleuri" },
    guenrouet43LaCassiere: { zip: "44530", city: "Guenrouet", address: "43 la cassiere" },
    pontchateauLaPintais: { zip: "44160", city: "Pontchateau", address: "La pintais" },
    saintAnneSurBrivet23LeGouletDeLaCrandelais: {
        zip: "44160",
        city: "Saint Anne sur brivet",
        address: "23 le goulet de la crandelais",
    },
    pierric3Renefort: { zip: "44290", city: "Pierric", address: "3 Renefort" },
    couffeLaCribotiere: { zip: "44521", city: "Couffé", address: "La cribotiere" },
    couffeLaCribotiere2: { zip: "44521", city: "Couffé", address: "La Cribotière" },
    treillieresLesFosses: { zip: "44119", city: "Treillieres", address: "Les fossés" },
    suceSurErdreLaCrubraie: { zip: "44240", city: "Sucé-sur-Erdre", address: "La crubraie" },
    saintEtienneDeMontluc9BisRueDuCheneCreux: {
        zip: "44360",
        city: "Saint Etienne de Montluc",
        address: "9 bis rue du Chêne creux",
    },
    blainLaGuerinais: { zip: "44130", city: "Blain", address: "La guerinais" },
    blainFermeDeLaConillais: { zip: "44130", city: "Blain", address: "Ferme de la conillais" },
    frossayLaRobinerie: { zip: "44320", city: "Frossay", address: "La Robinerie" },
    cheickEnRetz1LeBoisAuxMoines: {
        zip: "44640",
        city: "Cheick en retz",
        address: "1 Le Bois aux Moines",
    },
    cheickEnRetz2LeBoisAuMoine: {
        zip: "44640",
        city: "Cheick en retz",
        address: "2 le bois au moine",
    },
    cheixEnRetz1LaGrandeNoe: { zip: "44640", city: "Cheix en retz", address: "1 la grande noe" },
    pontSaintMartin25RueDeLaMone: {
        zip: "44860",
        city: "Pont Saint Martin",
        address: "25 rue de la mone",
    },
    saintJeanDeBoiseauCheminDesRocheres: {
        zip: "44640",
        city: "Saint jean de boiseau",
        address: "Chemin des rocheres",
    },
    laRemaudiere248LaChapelleAuxMoines: {
        zip: "44430",
        city: "La remaudiere",
        address: "248 la chapelle-aux-moines",
    },
    saintJulienDeConcelles41LEbrancherie: {
        zip: "44450",
        city: "Saint Julien de Concelles",
        address: "41 l'ebrancherie",
    },
    saintJulienDeConcelles41LEbrancherie2: {
        zip: "44450",
        city: "Saint julien de concelles",
        address: "41 l'ebrancherie",
    },
    petitAuverne4Heuretebise: { zip: "44670", city: "Petit auverne", address: "4 HEURETEBISE" },
    couffeLaCribotiere3: { zip: "44521", city: "Couffé", address: "La cribotière" },
    oudonRueDuChene: { zip: "44521", city: "Oudon", address: "Rue du Chêne" },
    leCellierCheminDesRigolets: { zip: "44850", city: "Le cellier", address: "Chemin des rigolets" },
    coueronLePetitBoisDesLoups: { zip: "44220", city: "Coueron", address: "Le petit bois des loups" },
    /**
     * Synthétique (#990) : zip/ville de commune inexistante — vérifié en
     * direct, 0 résultat IGN réel, distinct des deux autres known-hard
     * (input incomplet, score bas sur une adresse qui EXISTE).
     */
    nullepartvilleIntrouvable: {
        zip: "99999",
        city: "Nullepartville",
        address: "Route de Nulle Part",
    },
};
/**
 * Extension oracle-only de {@link JULIEN_KNOWN_HARD_ADDRESS_KEYS} — même
 * contrat (allowlist du générateur live, F2), sur les clés de
 * {@link JULIEN_ORACLE_AGENDA_ADDRESSES}.
 */
export const JULIEN_ORACLE_KNOWN_HARD_ADDRESS_KEYS = [
    "nullepartvilleIntrouvable",
];
export const JULIEN_KNOWN_HARD_LABELS = new Set([
    ...JULIEN_KNOWN_HARD_ADDRESS_KEYS.map((key) => labelOfAddress(JULIEN_AGENDA_ADDRESSES[key])),
    ...JULIEN_ORACLE_KNOWN_HARD_ADDRESS_KEYS.map((key) => labelOfAddress(JULIEN_ORACLE_AGENDA_ADDRESSES[key])),
]);
export const JULIEN_ORACLE_AGENDA = [
    {
        isoDate: "2026-07-21",
        events: [
            {
                id: "7d286e21",
                startTime: "08:30",
                endTime: "09:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.blain46LaColleEcurieDeLArros,
            },
            {
                id: "6a4fc38c",
                startTime: "11:00",
                endTime: "11:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.coueronRueDuPuigodeau,
            },
            {
                id: "fd4768f9",
                startTime: "11:30",
                endTime: "12:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.coueronRueDuMortierDesNouesFermeDuMarais,
            },
            {
                id: "cfb8ef4d",
                startTime: "12:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.coueronRueDuMortierDesNouesFermeDuMarais,
            },
            {
                id: "e02d2218",
                startTime: "13:15",
                endTime: "13:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.laChapelleLaunayLaHaieDeMaure,
            },
            {
                id: "4fe4d7e1",
                startTime: "14:15",
                endTime: "14:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pornichet40CheminDuClosRoux,
            },
        ],
    },
    {
        isoDate: "2026-07-22",
        events: [
            {
                id: "cb6ecdb9",
                startTime: "10:45",
                endTime: "11:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pornichet40CheminDuClosRoux,
            },
            {
                id: "312810a6",
                startTime: "12:00",
                endTime: "13:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintPereEnRetz1LaBasseParagere,
            },
            {
                id: "b6350708",
                startTime: "13:15",
                endTime: "13:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintPereEnRetzLeBoisRegnier,
            },
            {
                id: "aadd2197",
                startTime: "14:00",
                endTime: "14:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.corsept13BisLaBigotais,
            },
            {
                id: "1e25607f",
                startTime: "16:15",
                endTime: "16:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJeanDeBoiseauRueDesPicarderies,
            },
            {
                id: "52148619",
                startTime: "16:45",
                endTime: "17:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJeanDeBoiseauRueDesPicarderie,
            },
        ],
    },
    {
        isoDate: "2026-07-23",
        events: [
            {
                id: "7982dd57",
                startTime: "08:45",
                endTime: "09:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.marsacSurDon6LaunayDeBouin,
            },
            {
                id: "0972affe",
                startTime: "09:15",
                endTime: "10:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.marsacSurDon25LaunayDeBoin,
            },
            {
                id: "4b917d16",
                startTime: "12:30",
                endTime: "13:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.laChevallerais5LePatisJamet,
            },
            {
                id: "01770de2",
                startTime: "13:15",
                endTime: "13:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.blain46LaColle,
            },
        ],
    },
    {
        isoDate: "2026-07-27",
        events: [
            {
                id: "04571e45",
                startTime: "09:30",
                endTime: "10:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintMichelChefChefLaGrenouillere,
            },
            {
                id: "3342e163",
                startTime: "10:30",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintMichelChefChefMoulinDeLaSicaudais,
            },
            {
                id: "9dfeebfc",
                startTime: "11:00",
                endTime: "12:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintMichelChefChefMoulinDeLaSicaudais2,
            },
            {
                id: "2f23076e",
                startTime: "12:15",
                endTime: "12:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintMichelChefChefMoulinDeLaSicaudais,
            },
        ],
    },
    {
        isoDate: "2026-07-28",
        events: [
            {
                id: "01ab2d2c",
                startTime: "11:15",
                endTime: "13:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.leGavre18RueDesPuits,
            },
        ],
    },
    {
        isoDate: "2026-07-29",
        events: [
            {
                id: "fb31cc90",
                startTime: "09:30",
                endTime: "10:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.lesSorinieres4LaQuiltrie,
            },
            {
                id: "e4257f0a",
                startTime: "12:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeMoulinCasse,
            },
            {
                id: "0595d3ff",
                startTime: "13:00",
                endTime: "13:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeMoulinCasse2,
            },
            {
                id: "3b8e3a76",
                startTime: "13:30",
                endTime: "14:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeTertreJuin,
            },
            {
                id: "aa911154",
                startTime: "14:00",
                endTime: "15:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeTertreJuin2,
            },
            {
                id: "b5e40e82",
                startTime: "15:00",
                endTime: "15:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeTertreJuin3,
            },
        ],
    },
    {
        isoDate: "2026-07-30",
        events: [
            {
                id: "ef16d212",
                startTime: "10:00",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.bouinLesVerreries,
            },
            {
                id: "16d3b763",
                startTime: "11:15",
                endTime: "12:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.boisDeCeneLeMarcheClair,
            },
            {
                id: "f2b5b473",
                startTime: "12:30",
                endTime: "13:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintMemeLeBoisFoucaud,
            },
        ],
    },
    {
        isoDate: "2026-07-31",
        events: [
            {
                id: "d6ff95bc",
                startTime: "10:45",
                endTime: "11:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.plesseLaTahetrie,
            },
            {
                id: "44b08487",
                startTime: "11:45",
                endTime: "12:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.nortSurErdrePiaud,
            },
            {
                id: "8dd39fbf",
                startTime: "12:45",
                endTime: "13:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.treillieresFayau,
            },
            {
                id: "09e4a51d",
                startTime: "15:00",
                endTime: "16:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.carquefou12Maubreuil,
            },
        ],
    },
    {
        isoDate: "2026-08-03",
        events: [
            {
                id: "8f9b4633",
                startTime: "14:00",
                endTime: "14:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.vallonsDeLErdreLesBassesProvostieres,
            },
            {
                id: "8e6c561a",
                startTime: "15:00",
                endTime: "15:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.abbaretz7LaRainais,
            },
        ],
    },
    {
        isoDate: "2026-08-04",
        events: [
            {
                id: "2a2396a7",
                startTime: "10:30",
                endTime: "11:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.riailleLaVerrerie,
            },
            {
                id: "9a155660",
                startTime: "11:45",
                endTime: "12:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.erbray6LeBignon,
            },
            {
                id: "edd508b9",
                startTime: "14:00",
                endTime: "14:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.leGavre7RueDuVerreDeLaitLeHautLuc,
            },
        ],
    },
    {
        isoDate: "2026-08-05",
        events: [
            {
                id: "c4d19527",
                startTime: "09:00",
                endTime: "10:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pontSaintMartin30CheminDeLaVerdure,
            },
            {
                id: "d6961163",
                startTime: "11:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pontSaintMartinCheminDeLaVerdure,
            },
            {
                id: "5a15228c",
                startTime: "13:45",
                endTime: "15:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.laChapelleStFlorentRueDeLEvre,
            },
        ],
    },
    {
        isoDate: "2026-08-06",
        events: [
            {
                id: "38349398",
                startTime: "09:15",
                endTime: "09:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLeTertreJuin3,
            },
            {
                id: "61f21ac1",
                startTime: "10:15",
                endTime: "11:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJulienDeConcellesIleDeLaChesnaie,
            },
            {
                id: "beda4a4d",
                startTime: "11:45",
                endTime: "12:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.laChapelleHeulin7LaMorsonniere,
            },
            {
                id: "5cd90389",
                startTime: "13:00",
                endTime: "13:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.leLorouxBottereau84RouteDeLaCoulee,
            },
            {
                id: "1ab385d1",
                startTime: "14:00",
                endTime: "14:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.vertouRueDuChampFleuri,
            },
        ],
    },
    {
        isoDate: "2026-08-07",
        events: [
            {
                id: "1a7e2671",
                startTime: "09:30",
                endTime: "10:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.guenrouet43LaCassiere,
            },
            {
                id: "0be15825",
                startTime: "10:30",
                endTime: "11:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pontchateauLaPintais,
            },
            {
                id: "2d599c09",
                startTime: "11:30",
                endTime: "13:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintAnneSurBrivet23LeGouletDeLaCrandelais,
            },
            {
                id: "cb433b35",
                startTime: "13:45",
                endTime: "14:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pierric3Renefort,
            },
        ],
    },
    {
        isoDate: "2026-08-31",
        events: [
            {
                id: "a63dbe74",
                startTime: "10:30",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere,
            },
            {
                id: "be7b9952",
                startTime: "11:00",
                endTime: "11:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere,
            },
            {
                id: "6d876cdc",
                startTime: "12:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere2,
            },
            {
                id: "f821ec49",
                startTime: "13:15",
                endTime: "14:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.treillieresLesFosses,
            },
        ],
    },
    {
        isoDate: "2026-09-01",
        events: [
            {
                id: "e5d1ebac",
                startTime: "09:30",
                endTime: "11:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.nortSurErdrePiaud,
            },
        ],
    },
    {
        isoDate: "2026-09-02",
        events: [
            {
                id: "e4c9bd3f",
                startTime: "08:45",
                endTime: "10:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.suceSurErdreLaCrubraie,
            },
            {
                id: "94afe9b9",
                startTime: "10:30",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintEtienneDeMontluc9BisRueDuCheneCreux,
            },
            {
                id: "01b72537",
                startTime: "12:45",
                endTime: "13:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.blainLaGuerinais,
            },
        ],
    },
    {
        isoDate: "2026-09-03",
        events: [
            {
                id: "7857ad82",
                startTime: "09:15",
                endTime: "09:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.blainFermeDeLaConillais,
            },
        ],
    },
    {
        isoDate: "2026-09-07",
        events: [
            {
                id: "089851b1",
                startTime: "13:30",
                endTime: "14:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.frossayLaRobinerie,
            },
        ],
    },
    {
        isoDate: "2026-09-09",
        events: [
            {
                id: "b998c824",
                startTime: "09:45",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.cheickEnRetz1LeBoisAuxMoines,
            },
            {
                id: "5080b33f",
                startTime: "11:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.cheickEnRetz2LeBoisAuMoine,
            },
            {
                id: "4ca2a175",
                startTime: "12:45",
                endTime: "13:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.cheixEnRetz1LaGrandeNoe,
            },
            {
                id: "468ea558",
                startTime: "14:00",
                endTime: "14:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.pontSaintMartin25RueDeLaMone,
            },
        ],
    },
    {
        isoDate: "2026-09-10",
        events: [
            {
                id: "0b378c41",
                startTime: "13:00",
                endTime: "13:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJeanDeBoiseauCheminDesRocheres,
            },
            {
                id: "88eb45b6",
                startTime: "13:30",
                endTime: "14:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJeanDeBoiseauCheminDesRocheres,
            },
            {
                id: "0bd32888",
                startTime: "14:00",
                endTime: "14:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJeanDeBoiseauCheminDesRocheres,
            },
        ],
    },
    {
        isoDate: "2026-09-11",
        events: [
            {
                id: "eab5cd01",
                startTime: "12:30",
                endTime: "13:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.laRemaudiere248LaChapelleAuxMoines,
            },
            {
                id: "db5f05e8",
                startTime: "13:45",
                endTime: "14:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.vertouRueDuChampFleuri,
            },
            {
                id: "639be501",
                startTime: "14:45",
                endTime: "15:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJulienDeConcelles41LEbrancherie,
            },
            {
                id: "a1d411a3",
                startTime: "15:15",
                endTime: "15:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.saintJulienDeConcelles41LEbrancherie2,
            },
        ],
    },
    {
        isoDate: "2026-09-14",
        events: [
            {
                id: "cb062671",
                startTime: "09:30",
                endTime: "10:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.petitAuverne4Heuretebise,
            },
            {
                id: "ad78a9ab",
                startTime: "11:30",
                endTime: "12:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere2,
            },
            {
                id: "b982196a",
                startTime: "12:00",
                endTime: "12:30",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.couffeLaCribotiere3,
            },
            {
                id: "1f106c18",
                startTime: "12:45",
                endTime: "13:15",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.oudonRueDuChene,
            },
            {
                id: "3ca9d822",
                startTime: "13:30",
                endTime: "14:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.leCellierCheminDesRigolets,
            },
        ],
    },
    {
        isoDate: "2026-09-15",
        events: [
            {
                id: "0be9b5ee",
                startTime: "09:15",
                endTime: "09:45",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.coueronLePetitBoisDesLoups,
            },
        ],
    },
    /**
     * Journée synthétique (#990) — RDV à `nullepartvilleIntrouvable` (voir
     * commentaire ci-dessus). Sa `location: null` (D13) exclut tout candidat
     * voisin AVANT toute mesure de trajet — aucune leg n'est jamais routée
     * vers/depuis ce créneau, même mécanique que `incompleteBourgneuf` dans le
     * banc #478 (voir `engine.julienOracle.spec.ts`, garde-fou D13).
     */
    {
        isoDate: "2026-09-16",
        events: [
            {
                id: "990intr0",
                startTime: "10:00",
                endTime: "11:00",
                addr: JULIEN_ORACLE_AGENDA_ADDRESSES.nullepartvilleIntrouvable,
            },
        ],
    },
];
//# sourceMappingURL=scenarios.js.map